import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/*
 * User secured registration and login management system..
 *
 * Copyright (C) 2020 Adam van der Kruk aka TacB0sS
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { _className, Button, ComponentSync, Grid, LL_H_C, LL_V_C, TS_PropRenderer } from '@nu-art/thunderstorm-frontend/index';
import { _keys, addItemToArray, filterInstances } from '@nu-art/ts-common';
import { assertPasswordRules, PasswordAssertionType_LowerCaseLetters, PasswordAssertionType_MaxLength, PasswordAssertionType_MinLength, PasswordAssertionType_Numbers, PasswordAssertionType_SpecialChars } from '@nu-art/user-account-shared';
import { ModuleFE_Account, StorageKey_DeviceId } from '../_entity.js';
import { TS_Icons } from '@nu-art/ts-styles';
import { TS_InputV2 } from '@nu-art/thunderstorm-frontend/components/TS_V2_Input/index';
const form = {
    email: {
        className: '',
        type: 'text',
        hint: 'email',
        label: 'Email',
    },
    password: {
        type: 'password',
        hint: '****',
        label: 'Password',
    },
    passwordCheck: {
        type: 'password',
        hint: '****',
        label: 'Password Check',
    },
};
export class Component_Register extends ComponentSync {
    // ######################### Lifecycle #########################
    deriveStateFromProps(nextProps, state) {
        state.data ??= {};
        state.renderPasswordRules = nextProps.renderPasswordRules ?? false;
        if (state.renderPasswordRules) {
            state.passwordAssertionConfig = ModuleFE_Account.getPasswordAssertionConfig();
            state.passwordFailureReport = assertPasswordRules(state.data.password ?? '', state.passwordAssertionConfig);
        }
        return state;
    }
    // ######################### Logic #########################
    getPasswordRuleText = (key) => {
        const amount = this.state.passwordAssertionConfig?.[key];
        switch (key) {
            case PasswordAssertionType_LowerCaseLetters:
                return `At least ${amount} lower case letters`;
            case 'capital-letters':
                return `At least ${amount} capital letters`;
            case PasswordAssertionType_MaxLength:
                return `No more than ${amount} characters`;
            case PasswordAssertionType_MinLength:
                return `At least ${amount} characters`;
            case PasswordAssertionType_Numbers:
                return `At least ${amount} numbers`;
            case PasswordAssertionType_SpecialChars:
                return `At least ${amount} special characters`;
        }
    };
    onValueChanged = (value, id, onAccept = false) => {
        const data = { ...this.state.data };
        data[id] = value;
        if (id !== 'password')
            return this.setState({ data, errorMessages: undefined }, () => this.defaultFormStateUpdateCallback(onAccept));
        const passwordFailureReport = assertPasswordRules(value, this.state.passwordAssertionConfig);
        this.setState({
            data,
            errorMessages: undefined,
            passwordFailureReport
        }, () => this.defaultFormStateUpdateCallback(onAccept));
    };
    defaultFormStateUpdateCallback = (onAccept) => {
        if (!onAccept)
            return;
        if (!this.canSubmit())
            return;
        this.setState({ submitting: true }, async () => {
            await this.registerClicked();
            this.setState({ submitting: false });
        });
    };
    canSubmit = () => {
        if (this.state.passwordFailureReport)
            return;
        const data = this.state.data;
        const errors = filterInstances(_keys(form).map(key => {
            const field = form[key];
            return data[key] ? undefined : `missing ${field.label}`;
        }));
        const validateError = this.props.validate && this.props.validate(data);
        if (validateError)
            addItemToArray(errors, validateError);
        if (errors.length > 0) {
            this.setState({ errorMessages: errors });
            return false;
        }
        return true;
    };
    registerClicked = async () => {
        if (!this.canSubmit())
            return;
        try {
            await ModuleFE_Account._v1.registerAccount({
                ...this.state.data,
                deviceId: StorageKey_DeviceId.get()
            }).executeSync();
        }
        catch (_err) {
            const err = _err;
            this.setState({ errorMessages: [err.message] });
        }
    };
    // ######################### Render #########################
    render() {
        const data = this.state.data;
        return _jsxs(LL_V_C, { className: "ts-account__authenticate", children: [_keys(form).map((key, i) => {
                    const field = form[key];
                    return _jsxs(TS_PropRenderer.Vertical, { label: field.label, children: [_jsx(TS_InputV2, { id: key, value: data[key], type: field.type, allowAccept: true, onChange: (data) => this.onValueChanged(data, key), onAccept: (data) => this.onValueChanged(data, key, true) }), key === 'password' && this.renderPasswordRules()] }, i);
                }), this.renderErrorMessages(), _jsx(Button, { onClick: this.registerClicked, actionInProgress: this.state.submitting, className: `clickable ts-account__action-button`, disabled: !!this.state.passwordFailureReport, children: "Register" })] });
    }
    renderErrorMessages = () => {
        if (!this.state.errorMessages?.length)
            return '';
        return _jsx("ul", { className: 'ts-account__error-messages', children: this.state.errorMessages.map((message, i) => {
                return _jsx("li", { children: message }, i);
            }) });
    };
    renderPasswordRules = () => {
        if (!this.state.renderPasswordRules || !this.state.data.password)
            return;
        const assertionKeys = _keys(this.state.passwordAssertionConfig ?? {});
        if (!assertionKeys.length)
            return;
        return _jsx(Grid, { className: 'ts-account__authenticate__password-rules', children: assertionKeys.map(assertionKey => {
                const isFulfilled = !this.state.passwordFailureReport?.[assertionKey];
                const className = _className('ts-account__authenticate__password-rule', isFulfilled && 'valid');
                return _jsxs(LL_H_C, { className: className, children: [isFulfilled ? _jsx(TS_Icons.v.component, {}) : _jsx(TS_Icons.x.component, {}), this.getPasswordRuleText(assertionKey)] });
            }) });
    };
}
//# sourceMappingURL=Component_Register.js.map